import _, {truncate} from 'lodash';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {TimeFormatsEnum} from '../../../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Plural} from '../../../../../../../../libs/shared/core/src/helpers/internationalization/components/plural';
import {useToggle} from '../../../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {TeammateModel} from '../../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {DataEventActionsEnum} from '../../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {CommonSettingsDataEventObjectsEnum} from '../../../../../../../../libs/shared/core/src/models/tracking/objectNames/settingsDataEventObject';
import {useDataEventTracking} from '../../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Button} from '../../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {Icon} from '../../../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {TooltipCoordinator} from '../../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../../../libs/shared/ui/src/styles/mixins';
import {DateTimeFormatsEnum} from '../../../../../../../../srcCommon/formatters/dateTime/dateTimeFormatter';
import {ApplicationModel} from '../../../../../../../models/api/applicationModel';
import {TopicModel} from '../../../../../../../models/api/topicModel';
import {InvertedDateTimeFormatter} from '../../../../../../common/formatters/dateTime/invertedDateTimeFormatter';
import {
  AnnotationViewerReferencePreviewCopyButton,
  StyledCopyIconWrapper,
} from './annotationViewerReferencePreviewCopyButton';
import {CommentViewerReferencePreviewerUnfurlIcon} from './commentViewerReferencePreviewerUnfurlIcon';

/*
 * Constants.
 */

export enum TopicReferencePreviewerUnfurlTypesEnum {
  COMMENT = 'comment',
  MESSAGE = 'message',
  CONVERSATION_SUMMARY = 'conversation_summary',
}

export interface CustomFieldData {
  name: string;
  value: string;
  id: number;
}

const maxCustomFieldLength = 250;
const previewerWidth = 300;
const conversationDetailsPreviewerWidth = 426;
const horizontalPadding = 16;

/*
 * Props.
 */

interface TopicReferencePreviewerUnfurlProps extends TopicReferencePreviewerUnfurlBaseProps {
  type: TopicReferencePreviewerUnfurlTypesEnum;
  // These props are applicable when the type is MESSAGE and the previewer unfurl is possibly sandwiched
  // under a url and/or above a comment.
  isAboveComment?: boolean;
  isUnderUrl?: boolean;
}

export interface TopicReferencePreviewerUnfurlBaseProps {
  className?: string;
  topic?: TopicModel;
  owner?: TeammateModel;
  application?: ApplicationModel;
  customFieldDataArray: ReadonlyArray<CustomFieldData>;
  dateTimeFormat: DateTimeFormatsEnum;
  timeFormat: TimeFormatsEnum;
}

/*
 * Style.
 */

const conversationSummaryExtraWidth = 126;

interface StyledPreviewerWrapperDivProps {
  $type: TopicReferencePreviewerUnfurlTypesEnum;
  $isUnderUrl: boolean;
  $isAboveComment: boolean;
}

interface ConversationSummaryProps {
  $isConversationSummary: boolean;
}

const StyledPreviewerWrapperDiv = styled.div<StyledPreviewerWrapperDivProps>`
  ${(p) => computePreviewerWrapperDivStyles(p.$type, p.$isUnderUrl, p.$isAboveComment)}
`;

interface ScrollableDivProps {
  $isUnderUrl: boolean;
  $isAboveComment: boolean;
}

const ScrollableDiv = styled.div<ScrollableDivProps>`
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${(p) => (p.$isUnderUrl || p.$isAboveComment ? `288px` : `378px`)};
`;

function computePadding(isUnderUrl: boolean, isAboveComment: boolean) {
  // Topic annotation, url on top, and comment below.
  if (isUnderUrl && isAboveComment) {
    return `8px ${horizontalPadding}px 8px ${horizontalPadding}px`;
  }
  // Only topic annotation and url on top.
  if (isUnderUrl) {
    return `8px ${horizontalPadding}px 10px ${horizontalPadding}px`;
  }
  // Only topic annotation and comment below.
  if (isAboveComment) {
    return `0 ${horizontalPadding}px 8px ${horizontalPadding}px`;
  }
  // Only topic annotation.
  return `6px ${horizontalPadding}px ${horizontalPadding}px ${horizontalPadding}px`;
}

function computePreviewerWrapperDivStyles(
  type: TopicReferencePreviewerUnfurlTypesEnum,
  isUnderUrl: boolean,
  isAboveComment: boolean,
) {
  if (type === TopicReferencePreviewerUnfurlTypesEnum.COMMENT) {
    return css`
      position: relative;

      padding: ${horizontalPadding}px;
      background-color: ${(p) => p.theme.greys.white};

      margin: 4px 12px 12px 12px;
      width: ${previewerWidth}px;
      border-radius: 6px;
      max-height: 430px;
    `;
  }
  if (type === TopicReferencePreviewerUnfurlTypesEnum.CONVERSATION_SUMMARY) {
    return css`
      position: relative;

      padding: ${horizontalPadding}px;
      background-color: ${(p) => p.theme.greys.white};

      width: ${conversationDetailsPreviewerWidth}px;
      border-radius: 6px;
      max-height: 430px;
    `;
  }
  return css`
    padding: ${computePadding(isUnderUrl, isAboveComment)};
    max-height: 340px;
  `;
}

interface StyledPreviewerDivProps extends ConversationSummaryProps {
  $shouldAddBottomPadding: boolean;
}

const StyledPreviewerDiv = styled.div<StyledPreviewerDivProps>`
  display: flex;
  flex-flow: row;

  width: ${(p) =>
    p.$isConversationSummary
      ? `${previewerWidth - horizontalPadding * 2 + conversationSummaryExtraWidth}px`
      : `${previewerWidth - horizontalPadding * 2}px`};
  border-radius: 6px;
  gap: 4px;
  padding: ${(p) => (p.$shouldAddBottomPadding ? `0 0 8px 0` : ``)};
`;

const StyledIconWrapperDiv = styled.div`
  flex: 0;
  align-items: center;
`;

const StyledHeaderRowDiv = styled.div<ConversationSummaryProps>`
  flex: 1;
  overflow: hidden;

  height: 20px;
  line-height: 20px;
`;

export const StyledHeaderLinkDiv = styled.div`
  color: ${(p) => p.theme.palette.blue.shade40};
  font-size: ${fontSizes.medium};
  overflow: hidden;
  font-weight: ${fontWeights.semibold};

  &:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }
`;

interface StyledDescriptionWrapperDivProps extends ConversationSummaryProps {
  $hasCustomFields: boolean;
}

const StyledDescriptionWrapperDiv = styled.div<StyledDescriptionWrapperDivProps>`
  padding: ${(p) => (p.$hasCustomFields ? `0 0 8px 0` : ``)};
  max-width: ${(p) =>
    p.$isConversationSummary
      ? `${previewerWidth - horizontalPadding * 2 + conversationSummaryExtraWidth}px`
      : `${previewerWidth - horizontalPadding * 2}px`};
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-wrap: break-word;

  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 17px;
  text-align: left;
`;

const CustomFieldsWrapper = styled.div`
  position: relative;
`;

const NameDiv = styled.div`
  text-align: left;
  padding: 5px 0;

  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  ${ellipsis()};
`;

const ValueDiv = styled.div`
  display: flex;
`;

const StyledValueDiv = styled.div`
  padding: 5px 0;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: left;

  ${ellipsis()};
`;

const CustomFieldDiv = styled.div`
  display: grid;
  line-height: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  &:hover {
    ${StyledCopyIconWrapper} {
      display: flex;
    }
  }
`;

const StyledMoreButtonDiv = styled.div`
  width: fit-content;
  margin-left: -10px;
`;

const TooltipContentWrapper = styled.div`
  word-wrap: break-word;
`;

const StyledHeaderRowRightDiv = styled.div`
  flex: 0;
  display: flex;
`;

const StyledDateTimeText = styled.div`
  white-space: nowrap;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade70};

  margin-top: 1px;
`;

const StyledDateTimeIconDiv = styled.div`
  padding: 0 4px 0 4px;
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const TopicReferencePreviewerUnfurl: FC<TopicReferencePreviewerUnfurlProps> = (props) => {
  const {application, topic, className, customFieldDataArray, type, isAboveComment, isUnderUrl} = props;

  const hasCustomFields = customFieldDataArray.length > 0;
  const hasMoreThanFiveCustomFields = customFieldDataArray.length > 5;
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();

  const [shouldShowCheckmark, setShouldShowCheckmark] = useState(false);
  const [shouldShowExtraCustomFields, toggleShouldShowExtraCustomFields] = useToggle(false);

  if (!topic) {
    return null;
  }

  const isConversationSummary = type === TopicReferencePreviewerUnfurlTypesEnum.CONVERSATION_SUMMARY;

  return (
    <StyledPreviewerWrapperDiv
      $type={type}
      $isAboveComment={Boolean(isAboveComment)}
      $isUnderUrl={Boolean(isUnderUrl)}
    >
      <StyledPreviewerDiv
        className={className}
        $shouldAddBottomPadding={Boolean(topic.description)}
        $isConversationSummary={isConversationSummary}
      >
        <StyledIconWrapperDiv>
          <CommentViewerReferencePreviewerUnfurlIcon application={application} isAnnotationCard />
        </StyledIconWrapperDiv>
        <StyledHeaderRowDiv $isConversationSummary={isConversationSummary}>
          <StyledHeaderLinkDiv
            onClick={() => {
              navigateToLink(topic.externalLink);
              trackDataEvent(DataEventActionsEnum.OPEN_URL, CommonSettingsDataEventObjectsEnum.ANNOTATION, {
                topic_id: topic.id,
              });
            }}
          >
            <TooltipCoordinator render={() => topic.name} placement="top">
              <TooltipOverflowCondition>{topic.name}</TooltipOverflowCondition>
            </TooltipCoordinator>
          </StyledHeaderLinkDiv>
        </StyledHeaderRowDiv>
        <TooltipCoordinator
          placement="top"
          render={() => (
            <InvertedDateTimeFormatter
              timeFormat={props.timeFormat}
              value={topic.refreshedAt}
              format={props.dateTimeFormat}
            />
          )}
        >
          <StyledHeaderRowRightDiv>
            <StyledDateTimeIconDiv>
              <Icon name="repeatCircleSmall" />
            </StyledDateTimeIconDiv>
            <StyledDateTimeText>
              <RelativeDateTimeFormatter
                value={topic.refreshedAt}
                format={RelativeDateTimeFormatsEnum.SHORT}
              />
            </StyledDateTimeText>
          </StyledHeaderRowRightDiv>
        </TooltipCoordinator>
      </StyledPreviewerDiv>
      <ScrollableDiv $isAboveComment={Boolean(isAboveComment)} $isUnderUrl={Boolean(isUnderUrl)}>
        {topic.description && (
          <StyledDescriptionWrapperDiv
            $hasCustomFields={hasCustomFields}
            $isConversationSummary={isConversationSummary}
          >
            {topic.description}
          </StyledDescriptionWrapperDiv>
        )}
        {hasCustomFields && (
          <CustomFieldsWrapper>
            {_(customFieldDataArray)
              .take(shouldShowExtraCustomFields ? customFieldDataArray.length : 5)
              .map(({name, value, id}) => (
                <CustomFieldDiv onMouseLeave={() => setShouldShowCheckmark(false)} key={id}>
                  <NameDiv>
                    <TooltipCoordinator
                      isMultiline
                      render={() => (
                        <TooltipContentWrapper>
                          {truncate(name, {length: maxCustomFieldLength})}
                        </TooltipContentWrapper>
                      )}
                      placement="top"
                    >
                      <TooltipOverflowCondition>{name}</TooltipOverflowCondition>
                    </TooltipCoordinator>
                  </NameDiv>
                  <ValueDiv>
                    <StyledValueDiv>
                      <TooltipCoordinator
                        isMultiline
                        render={() => (
                          <TooltipContentWrapper>
                            {truncate(value, {length: maxCustomFieldLength})}
                          </TooltipContentWrapper>
                        )}
                        placement="top"
                      >
                        <TooltipOverflowCondition>{value}</TooltipOverflowCondition>
                      </TooltipCoordinator>
                    </StyledValueDiv>
                    <AnnotationViewerReferencePreviewCopyButton
                      value={value}
                      key={`${name}-${value}-${id}`}
                      shouldShowCheckmark={shouldShowCheckmark}
                      setShouldShowCheckmark={setShouldShowCheckmark}
                    />
                  </ValueDiv>
                </CustomFieldDiv>
              ))
              .value()}
          </CustomFieldsWrapper>
        )}
      </ScrollableDiv>
      {hasMoreThanFiveCustomFields && !shouldShowExtraCustomFields && (
        <StyledMoreButtonDiv>
          <Button
            size={VisualSizesEnum.SMALL}
            variant={ButtonVariantsEnum.LINK}
            onClick={toggleShouldShowExtraCustomFields}
          >
            <Plural
              id="topic-referencepreviewer-more"
              value={customFieldDataArray.length - 5}
              one="# more"
              other="# more"
            />
          </Button>
        </StyledMoreButtonDiv>
      )}
    </StyledPreviewerWrapperDiv>
  );
};
